import { useCallback, useMemo, useState } from "react";
import type { CSSProperties, PointerEvent as ReactPointerEvent } from "react";

type PointerType = "mouse" | "touch" | "pen";

interface UseSwipeToDismissOptions {
  onDismiss: () => void;
  enabled?: boolean;
  threshold?: number;
  fadeDistance?: number;
  pointerTypes?: PointerType[];
  allowFromInteractiveElements?: boolean;
}

interface SwipeHandlers {
  onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
  onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void;
}

const DEFAULT_POINTER_TYPES: PointerType[] = ["mouse", "touch", "pen"];

const isInteractiveElement = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  return Boolean(
    target.closest("button, a, input, select, textarea, [data-swipe-dismiss-ignore]")
  );
};

export function useSwipeToDismiss({
  onDismiss,
  enabled = true,
  threshold = 80,
  fadeDistance = 200,
  pointerTypes = DEFAULT_POINTER_TYPES,
  allowFromInteractiveElements = false,
}: UseSwipeToDismissOptions) {
  const [dragInfo, setDragInfo] = useState<{ startX: number; translate: number } | null>(null);
  const [isSwiping, setIsSwiping] = useState(false);

  const handlePointerDown: SwipeHandlers["onPointerDown"] = useCallback(
    (event) => {
      if (!enabled) return;
      if (!pointerTypes.includes(event.pointerType as PointerType)) return;
      if (!allowFromInteractiveElements && isInteractiveElement(event.target)) {
        return;
      }

      setDragInfo({ startX: event.clientX, translate: 0 });
      setIsSwiping(false);
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [allowFromInteractiveElements, enabled, pointerTypes]
  );

  const handlePointerMove: SwipeHandlers["onPointerMove"] = useCallback(
    (event) => {
      if (!dragInfo) return;
      const deltaX = event.clientX - dragInfo.startX;
      if (Math.abs(deltaX) > 6) {
        setIsSwiping(true);
      }
      setDragInfo((info) => (info ? { ...info, translate: deltaX } : info));
    },
    [dragInfo]
  );

  const handlePointerEnd: SwipeHandlers["onPointerUp"] = useCallback(
    (event) => {
      if (!dragInfo) return;
      event.currentTarget.releasePointerCapture(event.pointerId);
      const deltaX = dragInfo.translate;
      if (Math.abs(deltaX) > threshold) {
        onDismiss();
      }
      setDragInfo(null);
      setIsSwiping(false);
    },
    [dragInfo, onDismiss, threshold]
  );

  const swipeStyle: CSSProperties | undefined =
    dragInfo && isSwiping
      ? {
          transform: `translateX(${dragInfo.translate}px)`,
          opacity: 1 - Math.min(Math.abs(dragInfo.translate) / fadeDistance, 0.6),
        }
      : undefined;

  const swipeHandlers: SwipeHandlers = useMemo(
    () => ({
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: handlePointerEnd,
      onPointerCancel: handlePointerEnd,
    }),
    [handlePointerDown, handlePointerEnd, handlePointerMove]
  );

  return { swipeHandlers, swipeStyle, isSwiping };
}
